/* eslint-disable lingui/no-unlocalized-strings */

import { ReactNode, useCallback, useEffect, useState } from "react";
import webfontloader from "webfontloader";

import { t } from "@lingui/macro";
import { fonts } from "@reactive-resume/utils";
import { useResumeStore } from "@/client/stores/resume";
import type { ComboboxOption } from "@reactive-resume/ui";
import { Combobox, Label, Slider, Switch } from "@reactive-resume/ui";

import { SectionIcon } from "../shared/section-icon";
import { CollapsibleSection } from "../../CollapsibleSection";

const localFonts = ["Arial", "Cambria", "Garamond", "Times New Roman"];

const families = fonts.map((font) => ({
  value: font.family,
  label: <span style={{ fontFamily: font.family }}>{font.family}</span>, // having items displayed with specific style
})) satisfies ComboboxOption[];

families.push(
  ...localFonts.map((font) => ({
    value: font,
    label: <span style={{ fontFamily: font }}>{font}</span>, // having items displayed with specific style
  })),
);

export const TypographySection = () => {
  const [subsets, setSubsets] = useState<ComboboxOption[]>([]);
  const [variants, setVariants] = useState<ComboboxOption[]>([]);

  const setValue = useResumeStore((state) => state.setValue);
  const typography = useResumeStore((state) => state.resume.data.metadata.typography);

  useEffect(() => {
    const subsets = fonts.find((font) => font.family === typography.font.family)?.subsets ?? [];
    if (subsets.length > 0) {
      setSubsets(subsets.map((subset) => ({ value: subset, label: subset })));
    }

    const variants = fonts.find((font) => font.family === typography.font.family)?.variants ?? [];
    if (variants.length > 0) {
      setVariants(variants.map((variant) => ({ value: variant, label: variant })));
    }
  }, [typography.font.family]);

  const headerHTML: ReactNode = (
    <div className="flex items-center gap-x-4">
      <SectionIcon id="typography" size={18} name={t`Typography`} />
      <h2 className="line-clamp-1 text-base font-bold lg:text-lg">{t`Typography`}</h2>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className="grid gap-y-6">
      {/* Font Family - Dropdown with different font style items */}
      <div className="flex flex-col space-y-1.5">
        <Label>{t`Font Family`}</Label>
        <div className="w-full">
          <Combobox
            options={families}
            value={typography.font.family}
            searchPlaceholder={t`Search for a font family`}
            onValueChange={(selected) => {
              setValue("metadata.typography.font.family", selected);
              setValue("metadata.typography.font.subset", "latin");
              setValue("metadata.typography.font.variants", ["regular"]);
            }}
          />
        </div>
      </div>

      {/* Font Subset */}
      <div className="grid grid-cols-2 gap-x-4">
        <div className="space-y-1.5">
          <Label>{t`Font Subset`}</Label>
          <Combobox
            options={subsets}
            value={typography.font.subset}
            searchPlaceholder={t`Search for a font subset`}
            onValueChange={(value) => {
              setValue("metadata.typography.font.subset", value);
            }}
          />
        </div>

        {/* Font Variants */}
        <div className="space-y-1.5">
          <Label>{t`Font Variants`}</Label>
          <Combobox
            multiple
            options={variants}
            value={typography.font.variants}
            searchPlaceholder={t`Search for a font variant`}
            onValueChange={(value) => {
              setValue("metadata.typography.font.variants", value);
            }}
          />
        </div>
      </div>

      {/* Font Size */}
      <div className="space-y-1.5">
        <Label>{t`Font Size`}</Label>
        <div className="flex items-center gap-x-4 py-1">
          <Slider
            min={7}
            max={18}
            step={0.05}
            value={[typography.font.size]}
            onValueChange={(value) => {
              setValue("metadata.typography.font.size", value[0]);
            }}
          />

          <span className="text-base font-bold">{typography.font.size}</span>
        </div>
      </div>

      {/* Line Height */}
      <div className="space-y-1.5">
        <Label>{t`Line Height`}</Label>
        <div className="flex items-center gap-x-4 py-1">
          <Slider
            min={0}
            max={3}
            step={0.05}
            value={[typography.lineHeight]}
            onValueChange={(value) => {
              setValue("metadata.typography.lineHeight", value[0]);
            }}
          />

          <span className="text-base font-bold">{typography.lineHeight}</span>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-1.5">
        <Label>{t`Options`}</Label>

        <div className="flex items-center gap-x-4 py-1">
          <Switch
            id="metadata.typography.hideIcons"
            checked={typography.hideIcons}
            onCheckedChange={(checked) => {
              setValue("metadata.typography.hideIcons", checked);
            }}
          />
          <Label htmlFor="metadata.typography.hideIcons">{t`Hide Icons`}</Label>
        </div>

        <div className="flex items-center gap-x-4 py-1">
          <Switch
            id="metadata.typography.underlineLinks"
            checked={typography.underlineLinks}
            onCheckedChange={(checked) => {
              setValue("metadata.typography.underlineLinks", checked);
            }}
          />
          <Label htmlFor="metadata.typography.underlineLinks">{t`Underline Links`}</Label>
        </div>
      </div>
    </main>
  );

  return <CollapsibleSection id="typography" header={headerHTML} content={contentHTML} />;
};
